import { useId } from 'react';
import type { CSSProperties, ReactNode } from 'react';

export interface SwitchProps {
    checked?: boolean;
    onChange?: (checked: boolean) => void;
    label?: ReactNode;
    description?: ReactNode;
    disabled?: boolean;
    /** Nome acessível quando não há `label` visível. */
    ariaLabel?: string;
    style?: CSSProperties;
}

export function Switch({ checked = false, onChange, label, description, disabled = false, ariaLabel, style }: SwitchProps) {
    const idBase = useId();
    const idLabel = `${idBase}-label`;
    const idDescricao = `${idBase}-desc`;

    /* Era um <span role="switch"> com onClick: anunciado como interruptor, mas
       fora da ordem de tabulação e surdo ao teclado. Um <button> nativo resolve
       os dois de graça — foco, Enter e Espaço vêm do navegador. O rótulo deixou
       de ser <label> porque um botão dentro de <label> tem comportamento de
       clique ambíguo; a associação agora é por aria-labelledby. */
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, opacity: disabled ? 0.5 : 1, ...style }}>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-label={label ? undefined : ariaLabel}
                aria-labelledby={label ? idLabel : undefined}
                aria-describedby={description ? idDescricao : undefined}
                disabled={disabled}
                onClick={() => onChange?.(!checked)}
                style={{
                    width: 34, height: 20, borderRadius: 'var(--radius-pill)', flex: '0 0 auto',
                    background: checked ? 'var(--burgundy-700)' : 'var(--neutral-300)',
                    border: 0, padding: 0, position: 'relative',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'background-color var(--dur-base) var(--ease-standard)',
                }}
            >
                <span
                    aria-hidden="true"
                    style={{
                        position: 'absolute', top: 2, left: checked ? 16 : 2, width: 16, height: 16, borderRadius: '50%',
                        background: '#fff', boxShadow: 'var(--shadow-xs)', transition: 'left var(--dur-base) var(--ease-standard)',
                    }}
                />
            </button>
            {label && (
                <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <span id={idLabel} style={{ fontSize: 14, lineHeight: '20px', color: 'var(--text-primary)' }}>{label}</span>
                    {description && (
                        <span id={idDescricao} style={{ fontSize: 12.5, lineHeight: '17px', color: 'var(--text-secondary)' }}>{description}</span>
                    )}
                </span>
            )}
        </span>
    );
}
